import React, { useEffect, useRef } from 'react';
import {
	fitnessProfileFormErrorClassName,
	fitnessProfileModalBackdropClassName,
	fitnessProfileModalPanelClassName,
	fitnessProfileDateFieldClassName,
	fitnessProfileDateInputClassName,
	readingModalInputClassName
} from './shared/fitnessProfileReadingStyles';

export default function FitnessProfileWeightLogModal({
	isOpen,
	onClose,
	onSave,
	form,
	onFormChange,
	error,
	saving
}) {
	const poundsInputRef = useRef(null);

	useEffect(() => {
		if (!isOpen) return undefined;

		const frameId = requestAnimationFrame(() => {
			const input = poundsInputRef.current;
			if (!input) return;
			input.focus();
			if (input.value) input.select();
		});

		return () => cancelAnimationFrame(frameId);
	}, [isOpen]);

	if (!isOpen) return null;

	const isEditing = Boolean(form.editingId);

	function handleSubmit(event) {
		event.preventDefault();
		if (!saving) onSave();
	}

	return (
		<div
			className={`${fitnessProfileModalBackdropClassName} max-w-none`}
			onClick={onClose}
			role="presentation"
		>
			<div
				className={`${fitnessProfileModalPanelClassName} max-w-md`}
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="weight-log-modal-title"
			>
				<div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-dark-border pb-3">
					<h3 id="weight-log-modal-title" className="text-lg font-semibold text-gray-900 dark:text-dark-text">
						{isEditing ? 'Edit Weight' : 'Log Weight'}
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="flex h-8 w-8 items-center justify-center rounded-full text-xl font-semibold leading-none text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg hover:text-gray-700 dark:hover:text-dark-text transition"
						aria-label="Cancel"
					>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit} className="min-w-0">
					<div className="mt-4 space-y-4">
						<div className="min-w-0 w-full">
							<label
								htmlFor="weight-log-date"
								className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary"
							>
								Date
							</label>
							<div className={fitnessProfileDateFieldClassName}>
								<input
									id="weight-log-date"
									type="date"
									value={form.date}
									onChange={(event) => onFormChange('date', event.target.value)}
									className={fitnessProfileDateInputClassName}
								/>
							</div>
						</div>

						<div>
							<label
								htmlFor="weight-log-pounds"
								className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary"
							>
								Weight (lbs)
							</label>
							<input
								ref={poundsInputRef}
								id="weight-log-pounds"
								type="number"
								inputMode="decimal"
								min={50}
								max={600}
								step="0.1"
								placeholder="178"
								value={form.pounds}
								onChange={(event) => onFormChange('pounds', event.target.value)}
								className={`mt-1 text-center text-lg font-semibold py-2.5 ${readingModalInputClassName}`}
							/>
						</div>

						{error && (
							<p className={fitnessProfileFormErrorClassName} role="alert">
								{error}
							</p>
						)}
					</div>

					<div className="mt-5 flex justify-end border-t border-gray-200 dark:border-dark-border pt-4">
						<button
							type="submit"
							disabled={saving}
							className="inline-flex min-w-24 items-center justify-center rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 transition"
						>
							{saving ? 'Saving…' : isEditing ? 'Update' : 'Save'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
